import React, { useContext, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { AuthContext } from "../context/AuthContext";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";

function SellThruReportTool() {
    const [linesheetSource, setLinesheetSource] = useState("google");
    const [linesheet, setLinesheet] = useState(null);
    const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
    
    const [inventorySource, setInventorySource] = useState("upload");
    const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
      location: "Main Store",
      vendors: "",
      excludeArchive: true,
      excludeRS: true
    });
    const [inventory, setInventory] = useState(null);
    const [fetchingShopify, setFetchingShopify] = useState(false);
    
    const [orders, setOrders] = useState(null);
    const [sheetName, setSheetName] = useState("");
    const [rowsToProcess, setRowsToProcess] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const { addToast } = useToast();
    const { user } = useContext(AuthContext);
    const [showInfoModal, setShowInfoModal] = useState(false);

    const toolInfo = {
        toolName: "Sell-Thru Report",
        description: "Analyze sales performance by comparing inventory, orders, and linesheets. Calculates sell-through rates for variants.",
        steps: [
            "Choose a shared Google Sheet or upload your Linesheet (Excel).",
            "Upload your Inventory file (Excel).",
            "Upload your Orders file (Excel or CSV).",
            "Enter the specific Sheet Name from the Linesheet (or leave blank for all).",
            "Click 'Generate Report' to download the analysis."
        ],
        templates: [
            { name: "Linesheet Template", link: "/templates/download/template_linesheet.xlsx" },
            { name: "Inventory Template", link: "/templates/download/template_inventory.xlsx" },
            { name: "Orders Template", link: "/templates/download/template_orders.xlsx" }
        ]
    };

    const guideTips = [
        "Choose a single vendor tab for a focused report, or leave the sheet name blank to process every non-empty tab.",
        "Shared Google Sheets are best when the linesheet is updated by multiple people during the week.",
        "Orders exports can be CSV or Excel, but the file must include Variant SKU values.",
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        const hasLinesheet =
            linesheetSource === "google"
                ? Boolean(selectedGoogleSheet?.id)
                : Boolean(linesheet);

        if (!hasLinesheet || !orders) {
            addToast("Please provide a linesheet source and an orders file.", "error");
            return;
        }
        
        if (inventorySource === "upload" && !inventory) {
            addToast("Please upload an inventory file or select Shopify sync.", "error");
            return;
        }

        const MAX_SIZE = 100 * 1024 * 1024;
        if (
            (linesheetSource === "upload" && linesheet && linesheet.size > MAX_SIZE) ||
            (inventorySource === "upload" && inventory && inventory.size > MAX_SIZE) ||
            (orders && orders.size > MAX_SIZE)
        ) {
            addToast("One or more files are too large. Maximum size is 100MB.", "error");
            return;
        }
        
        let finalInventoryFile = inventory;
        
        if (inventorySource === "shopify") {
            setFetchingShopify(true);
            try {
                const excludeTags = [];
                if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
                if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");
                
                const vendors = inventoryShopifyConfig.vendors
                  ? inventoryShopifyConfig.vendors.split(",").map(v => v.trim()).filter(v => v)
                  : [];
                  
                const locationNames = inventoryShopifyConfig.location === "All" 
                  ? [] 
                  : [inventoryShopifyConfig.location];

                const shopifyRes = await flaskApi.post(
                  "/shopify/fetch-inventory-excel", 
                  { vendors, exclude_tags: excludeTags, location_names: locationNames },
                  { responseType: "blob" }
                );
                
                finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                });
                
            } catch (err) {
                setFetchingShopify(false);
                const errorMessage = await extractErrorMessage(err, "Failed to fetch inventory from Shopify.");
                addToast(errorMessage, "error");
                return;
            }
            setFetchingShopify(false);
        }

        const formData = new FormData();
        if (linesheetSource === "google") {
            formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
            formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
        } else {
            formData.append("linesheet", linesheet);
        }
        formData.append("inventory", finalInventoryFile);
        formData.append("orders", orders);
        if (sheetName) {
            formData.append("linesheet_sheet", sheetName);
        }
        if (rowsToProcess) {
            formData.append("rows_to_process", rowsToProcess);
        }

        setLoading(true);
        setUploadProgress(0);

        try {
            const response = await flaskApi.post(
                "/sellthru/build",
                formData,
                {
                    responseType: "blob",
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);
                    }
                }
            );

            if (
                response.data.type === "application/json" ||
                (response.headers["content-type"] &&
                    response.headers["content-type"].includes("application/json"))
            ) {
                const text = await response.data.text();
                const json = JSON.parse(text);
                addToast(`Error: ${json.error}`, "error");
                return;
            }

            const blob = new Blob([response.data], {
                type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const contentDisposition = response.headers["content-disposition"];
            const extractedFilename = extractFilenameFromHeader(contentDisposition);
            const filename = extractedFilename || "SellThru_Report.xlsx";

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            if (linesheetSource === "google" && selectedGoogleSheet?.id) {
                markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
            }

            addToast("Report downloaded successfully.", "success");
        } catch (err) {
            const errorMessage = await extractErrorMessage(err, "Error generating sell-thru report.");
            addToast(errorMessage, "error");
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    return (
        <>
            <ToolPageLayout
                title="Sell-Thru Report"
                subtitle={toolInfo.description}

                steps={toolInfo.steps}
                templates={toolInfo.templates}
                tips={guideTips}
                onOpenInfo={() => setShowInfoModal(true)}
            >
                <form onSubmit={handleSubmit} className="tool-form tool-form-split">
                    <div className="tool-form-main">
                        <section className="tool-section">
                            <div className="tool-section-header">
                                <h3 className="tool-section-title">Report Inputs</h3>
                            </div>

                            <div className="tool-field-stack">
                                <ToolFileUploadField
                                    label="Upload Orders File (Max 100MB)"
                                    file={orders}
                                    setFile={setOrders}
                                />
                            </div>

                            <div className="tool-field-stack">
                                <label className="tool-label">Linesheet Tab Name</label>
                                <input
                                    type="text"
                                    className="tool-input"
                                    value={sheetName}
                                    onChange={(e) => setSheetName(e.target.value)}
                                    placeholder="e.g. Birkenstock"
                                />
                            </div>

                            <div className="tool-field-stack">
                                <label className="tool-label">Rows to Process (Optional)</label>
                                <input
                                    type="text"
                                    className="tool-input"
                                    value={rowsToProcess}
                                    onChange={(e) => setRowsToProcess(e.target.value)}
                                    placeholder="e.g. 6-10, 12, 14"
                                />
                            </div>
                        </section>

                        <section className="tool-section">
                            <div className="tool-section-header">
                                <h3 className="tool-section-title">Inventory Source</h3>
                            </div>
                            <InventorySourceSelector
                                source={inventorySource}
                                onSourceChange={setInventorySource}
                                uploadFile={inventory}
                                onUploadFileChange={setInventory}
                                shopifyConfig={inventoryShopifyConfig}
                                onShopifyConfigChange={setInventoryShopifyConfig}
                            />
                        </section>

                        <div className="tool-action-stack">
                            <button
                                type="submit"
                                className="tool-btn-primary"
                                disabled={loading || fetchingShopify}
                            >
                                {loading || fetchingShopify ? "Processing..." : "Generate Report"}
                            </button>

                            <ToolProgressPanel
                                loading={loading}
                                isDownloadingShopify={fetchingShopify}
                                uploadProgress={uploadProgress}
                                uploadMessage="Uploading linesheet, inventory, and orders..."
                                processingMessage="Generating Sell-Thru Report..."
                            />
                        </div>
                    </div>

                    <div className="tool-form-side">
                        <section className="tool-section">
                            <div className="tool-section-header">
                                <h3 className="tool-section-title">Linesheet Source</h3>
                            </div>
                            <LinesheetSourceSelector
                                source={linesheetSource}
                                onSourceChange={setLinesheetSource}
                                uploadFile={linesheet}
                                onUploadFileChange={setLinesheet}
                                selectedGoogleSheet={selectedGoogleSheet}
                                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                                uploadLabel="Upload Linesheet (Max 100MB)"
                            />
                        </section>
                    </div>
                </form>
            </ToolPageLayout>

            <ToolInfoModal
                isOpen={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                toolName={toolInfo.toolName}
                description={toolInfo.description}
                steps={toolInfo.steps}
                templates={toolInfo.templates}
            />
        </>
    );
}

export default SellThruReportTool;
